<template>
  <new-modal-dialog
    :title="`${$t('title.newsletter')} - ${campaign.title}`"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    max-width="1300px"
    scrollable
    fluid
    @close="emit('close')"
  >
    <v-row>
      <v-col
        cols="6"
        class="py-0 pb-2"
      >
        <v-text-field
          :value="campaign.contactFilter?.text"
          class="w-100 readonly--disabled"
          persistent-hint
          readonly
          hide-details
          :label="$t('label.audience')"
          :suffix="`(${$tc('label.x_recipients', campaign.recipientCount)})`"
        />
      </v-col>
      <v-col
        cols="6"
        class="py-0 pb-2"
      >
        <v-text-field
          :value="campaign.locale"
          class="w-100 readonly--disabled"
          hide-details
          readonly
          :label="$t('label.language')"
        />
      </v-col>
      <v-col
        cols="6"
        class="py-0 pb-2"
      >
        <v-text-field
          :value="$localized.numericDateTimeText(campaign.dtSend)"
          class="my-2 w-100 readonly--disabled"
          hide-details
          readonly
          :label="$t('label.sent_on')"
        />
      </v-col>
      <v-col
        cols="6"
        class="py-0 pb-2"
      >
        <p class="subheading pb-1 mb-0">
          {{ $t('label.status') }}
        </p>
        <v-chip
          v-if="campaign.status && campaign.status !== MarketingCampaignStatus.None"
          :color="colorForCampaignStatus(campaign.status)"
          class="black--text"
          small
          label
        >
          {{ $t(`code.newsletter_campaign_status.${campaign.status}`) }}
        </v-chip>
      </v-col>
      <v-col
        cols="12"
        md="6"
        lg="8"
        class="py-0 pb-2"
      >
        <p class="subheading my-2">
          {{ $t('label.newsletter_email_preview') }}
        </p>
        <email-preview-frame
          style="overflow: hidden;"
          :subject="campaign.subject"
        >
          <template #toolbar-items>
            <v-spacer />
            <v-btn
              small
              outlined
              color="green"
              @click="saveAsTemplate"
            >
              {{ $t('button.save_to_design_library') }}
            </v-btn>
          </template>
          <html-image :html="campaign.templatePreview ?? undefined" />
        </email-preview-frame>
      </v-col>

      <v-col
        cols="12"
        md="6"
        lg="4"
        class="py-0 pb-2"
      >
        <p class="subheading my-2">
          {{ $t('label.newsletter_results') }}
        </p>
        <newsletter-funnel-chart
          :dataset="charts.funnel"
          :stats="charts.stats"
        />
        <v-divider class="mt-8 mb-2" />
        <p class="subheading my-2">
          {{ $t('label.newsletter_unsubscribers') }}
        </p>
        <newsletter-gauge-chart :value="charts.gauge" />
      </v-col>
    </v-row>

    <template #actions>
      <v-spacer />
      <v-btn
        text
        color="error"
        @click="$emit('close')"
      >
        {{ $t('button.close') }}
      </v-btn>
    </template>
  </new-modal-dialog>
</template>

<script setup lang="ts">

import HtmlImage from '@/components/util/HtmlImage.vue';
import NewModalDialog from '@/components/dialogs/NewModalDialog.vue';
import {
  PropType, computed, onMounted, ref,
} from 'vue';
import i18n from '@/plugins/i18n';
import MarketingCampaign, { MarketingCampaignStatus } from '@/grpc-api/model/marketing-campaign';
import nameWithCopyText from '@/util/copy-name';
import HtmlTemplate from '@/grpc-api/model/html-template';
import { performSaveAction } from '@/services/vue-utils';
import store from '@/store/store';
import router from '@/router';
import EmailPreviewFrame from './EmailPreviewFrame.vue';
import NewsletterFunnelChart from './NewsletterFunnelChart.vue';
import NewsletterGaugeChart from './NewsletterGaugeChart.vue';
import { colorForCampaignStatus } from './newsletter';

const emit = defineEmits(['close']);

const props = defineProps({
  campaign: { type: Object as PropType<MarketingCampaign>, required: true },
});

const saveAsTemplate = async () => {
  if (!props.campaign.templateDesign) return;

  const name = nameWithCopyText(props.campaign.templateName ?? 'new', i18n.t('label.copy_of') as string);
  const htmlTemplate = new HtmlTemplate({
    name,
    design: props.campaign.templateDesign,
    html: props.campaign.templateHtml ?? undefined,
    preview: props.campaign.templatePreview ?? undefined,
  });

  const ok = await performSaveAction(
    undefined,
    async () => store.dispatch('marketingCampaignManagerCreateHtmlTemplate', { htmlTemplate }),
  );

  if (ok) {
    router.replace('/newsletter/library');
  }
};

const charts = computed(() => {
  type P = 'scheduled'|'sent'|'processing'|'abandoned'|'failure'|'all';
  const ps = (t: P) => props.campaign.publishStats![t] ?? 0;
  type D = 'processed'|'dropped'|'delivered'| 'deferred'| 'bounce'| 'blocked';
  const ds = (t: D) => props.campaign.deliveryStats![t] ?? 0;
  type E = 'open'|'unique_open'|'click'|'unique_click'|'spam'|'unsubscribe'|'group_unsubscribe'|'group_resubscribe';
  const es = (t: E) => props.campaign.engagementStats![t] ?? 0;
  type W = 'widget_open'|'unique_widget_open'|'widget_reservation'|'unique_widget_reservation'|'widget_unsubscribe';
  const ws = (t: W) => props.campaign.engagementStats![t] ?? 0;
  type C = 'all'|'imminent'|'unique_all'|'unique_imminent'|'guests_all'|'guests_imminent';
  const cs = (t: C) => props.campaign.conversionStats![t] ?? 0;

  // eslint-disable-next-line vue/max-len
  const fvalues = [ps('all'), ps('sent'), ds('delivered'), es('unique_open'), es('unique_click')];
  for (let i = fvalues.length - 2; i >= 0; i -= 1) if (fvalues[i] < fvalues[i + 1]) fvalues[i] = fvalues[i + 1];
  const [all, sent, delivered, uniqueOpen, uniqueClick] = fvalues;
  const uniqueReservations = cs('unique_imminent');
  const unsubscribe = es('unsubscribe') + ws('widget_unsubscribe');
  const reservations = cs('imminent');
  const guests = cs('guests_imminent');

  const fv = (now: number, then: number) => [now, then > 0 ? (now / then) * 100 : 0];
  const funnel = [
    { value: fv(sent, all), name: i18n.t('label.newsletters_sent') },
    { value: fv(delivered, sent), name: i18n.t('label.newsletters_delivered') },
  ];

  if (delivered > 0) {
    funnel.push({ value: fv(uniqueOpen, delivered), name: i18n.t('label.newsletters_opened') });
  }
  if (uniqueOpen > 0) {
    funnel.push({ value: fv(uniqueClick, uniqueOpen), name: i18n.t('label.newsletters_clicked') });
  }
  if (uniqueClick > 0) {
    funnel.push({ value: fv(reservations, uniqueClick), name: i18n.t('label.reservations') });
  }

  const gauge = delivered > 0 ? (unsubscribe / delivered) : 0;

  const rv = (nom: number, den: number) => (den > 0 ? `${Math.floor((nom / den) * 1000) / 10}%` : '0%');
  const stats = {
    or: rv(uniqueOpen, delivered),
    ctr: rv(uniqueClick, delivered),
    ctor: rv(uniqueClick, uniqueOpen),
    cr: rv(uniqueReservations, delivered),
    tr: `${reservations}`,
    tg: `${guests}`,
  };

  return { funnel, gauge, stats };
});

onMounted(() => {
  console.log('campaign', props.campaign);
});

</script>
